/**
 * FlashBook clips — where a recording actually lives.
 *
 * A clip starts life as a URL only this tab can resolve: `blob:` for a render
 * the desk just produced, `file:` for a take the microphone just wrote. That is
 * the right shape for the second between the microphone stopping and the block
 * being saved, and the wrong shape for anything after it. A page whose audio is
 * a `blob:` URL is a page that goes silent for every reader — including its
 * author, on their next device, and on this one after a reload, because blob
 * URLs do not survive the tab that minted them.
 *
 * So a clip becomes an *asset* as soon as it exists: the field uploads the bytes
 * and the block keeps the upload URL from then on (see `FlashBookAudioField`).
 * Two small, device-local things live here to support that:
 *
 *  • the vocabulary for asking whether a URL is an asset or only a tab
 *    (`isDurableClipUrl` / `isLocalClipUrl`), used by the field *and* by the save
 *    pipeline, so one definition decides what still needs uploading;
 *  • a session cache from an asset URL back to the local bytes we already hold
 *    (`clipSourceFor`), so the desk never downloads a take the creator recorded a
 *    minute ago — and can still open it with the network unplugged.
 *
 * Deliberately not persisted: the cache is an optimisation, and the authority is
 * the asset URL that the block carries.
 */

/** Schemes — and paths — that only mean something on the device that wrote them. */
export function isLocalClipUrl(url?: string | null): boolean {
  if (!url) return false
  return (
    url.startsWith('file:') ||
    url.startsWith('data:') ||
    url.startsWith('blob:') ||
    // A relative path resolves against whichever deployment is serving the app,
    // which is not the same thing as "wherever this creator is".
    url.startsWith('/')
  )
}

/**
 * Whether a clip is something the world can fetch.
 *
 * Any absolute `http(s)` URL counts, including the PDS's `getBlob` URL: the
 * point is not *whose* server it is, it is that the clip no longer depends on
 * the tab that made it.
 */
export function isDurableClipUrl(url?: string | null): boolean {
  if (!url || isLocalClipUrl(url)) return false
  return /^https?:\/\//i.test(url)
}

/**
 * How many local clips to keep mirrored.
 *
 * Each entry is only a reference to bytes the browser already holds, so this is
 * cheap — but a creator will re-record and re-apply many times in one sitting,
 * and every apply mints a fresh blob. Twelve covers a book's page being worked
 * on several times over without holding a library.
 */
const MAX_LOCAL_CLIPS = 12

/** Asset URL → the local URL whose bytes are the same clip. */
const localClips = new Map<string, string>()

/**
 * Notes that the bytes behind `assetUrl` are already here, at `localUrl`.
 *
 * Called at the one moment both are known: just after the upload of a clip
 * returns its asset URL, while the recorded blob is still in hand.
 */
export function rememberLocalClip(assetUrl: string, localUrl: string): void {
  if (!assetUrl || !localUrl || !isDurableClipUrl(assetUrl)) return
  localClips.set(assetUrl, localUrl)
  while (localClips.size > MAX_LOCAL_CLIPS) {
    const oldest = localClips.keys().next().value
    if (oldest === undefined || oldest === assetUrl) break
    localClips.delete(oldest)
  }
}

/** The local URL holding this asset's bytes, when this device still has them. */
export function localClipFor(assetUrl?: string | null): string | undefined {
  if (!assetUrl) return undefined
  return localClips.get(assetUrl)
}

/** Drops a mirror (the bytes are gone — revoked, or the take was replaced). */
export function forgetLocalClip(assetUrl: string): void {
  localClips.delete(assetUrl)
}

/**
 * The URL to actually read a clip from: the local bytes when we hold them, and
 * the asset URL otherwise.
 *
 * Every reader of a clip's *bytes* goes through this. Playback does not need to:
 * an `<audio>` element fetching the asset URL is exactly what the learner's
 * device does, and it is worth the app doing the same so the creator hears the
 * same thing the reader will.
 */
export function clipSourceFor(url: string): string {
  return localClipFor(url) ?? url
}

/**
 * What to do about a clip's durability, as one decision rather than three.
 *
 * The field asks this the moment a clip appears and whenever the clip changes;
 * naming the cases keeps the two easy ones (`asset`, `offline`) from growing
 * into conditions inside a component, and — more importantly — makes the
 * signed-out case explicit: a creator with no session is *silent*, not reassured,
 * because there is genuinely nothing keeping their take but this device.
 */
export type ClipDurability =
  | {kind: 'asset'}
  | {kind: 'offline'}
  /** Both are present — which is exactly what a caller needs to act on it. */
  | {kind: 'upload'; url: string; did: string}

export function clipDurability(
  url?: string | null,
  did?: string | null,
): ClipDurability {
  if (!url || isDurableClipUrl(url)) return {kind: 'asset'}
  // No session: a draft's media is uploaded when the book is saved instead, so
  // the fallback exists — it just is not this field's job.
  if (!did) return {kind: 'offline'}
  return {kind: 'upload', url, did}
}

/** Testing seam — the cache is process-wide by design. */
export function clearLocalClips(): void {
  localClips.clear()
}
